"use client"

import { useState } from "react"
import Link from "next/link"
import { formatValue } from "@/lib/utils"

const SIZES = [10, 25, 50] as const
const DEFAULT_SIZE = 10

export interface TopListItem {
  name: string
  slug: string | null
  value: number
  description: string | null
}

interface TopListRankingProps {
  items: TopListItem[]
  playHref: string
  valueUnit: string | null
  valuePrefix: boolean
}

/**
 * The size selector used to be a `?n=` link, which forced the whole route to
 * render dynamically and skip ISR. Holding the count in client state keeps the
 * toggle instant, keeps the route static, and leaves every ranked row in the
 * server-rendered HTML for crawlers regardless of what is visible.
 */
export function TopListRanking({
  items,
  playHref,
  valueUnit,
  valuePrefix,
}: TopListRankingProps) {
  const [visibleCount, setVisibleCount] = useState<number>(DEFAULT_SIZE)
  const maxVal = items[0]?.value ?? 1

  return (
    <>
      <div className="flex items-center gap-2">
        {SIZES.map((size) => (
          <button
            key={size}
            type="button"
            onClick={() => setVisibleCount(size)}
            aria-pressed={visibleCount === size}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              visibleCount === size
                ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-white"
            }`}
          >
            Top {size}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {items.map((item, i) => {
          const pct = (item.value / maxVal) * 100
          const isTop3 = i < 3
          return (
            <div
              key={`${item.slug ?? item.name}-${i}`}
              hidden={i >= visibleCount}
              className={`group p-4 rounded-xl border transition-all ${isTop3 ? "bg-zinc-900 border-zinc-700" : "bg-zinc-900/50 border-zinc-800"}`}
            >
              <div className="flex items-center gap-4">
                <span
                  className={`w-10 text-right font-mono font-bold ${
                    i === 0
                      ? "text-amber-400 text-xl"
                      : i === 1
                        ? "text-zinc-300 text-lg"
                        : i === 2
                          ? "text-amber-600 text-lg"
                          : "text-zinc-500 text-sm"
                  }`}
                >
                  #{i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    {item.slug ? (
                      <Link
                        href={playHref}
                        className="text-white font-medium hover:text-amber-400 transition-colors truncate"
                      >
                        {item.name}
                      </Link>
                    ) : (
                      <span className="text-white font-medium truncate">{item.name}</span>
                    )}
                    <span
                      className={`ml-2 shrink-0 font-semibold ${isTop3 ? "text-white" : "text-zinc-400"}`}
                    >
                      {formatValue(item.value, valueUnit || undefined, valuePrefix)}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        i === 0
                          ? "bg-gradient-to-r from-amber-500 to-amber-400"
                          : i < 3
                            ? "bg-amber-500/60"
                            : "bg-zinc-600"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {item.description && isTop3 && (
                    <p className="text-xs text-zinc-500 mt-1 line-clamp-1">{item.description}</p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}
